/**
 * Converts an ISO date string or YYYY-MM format to YYYY-MM format
 * @param dateString The date string (ISO format or YYYY-MM format)
 * @returns Date string in YYYY-MM format
 */
function normalizeDate(dateString?: string | Date | null): string {
  if (!dateString) return '';

  // Convert to string if it's a Date object
  if (dateString instanceof Date) {
    const year = dateString.getFullYear();
    const month = (dateString.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  }

  // Ensure it's a string
  const dateStr = String(dateString);

  // If it's already in YYYY-MM format, return as is
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }

  // If it's an ISO date string, convert to YYYY-MM
  if (dateStr.includes('T') || dateStr.includes('Z')) {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  }

  // If it's in MM/YYYY format (legacy), convert to YYYY-MM
  if (dateStr.includes('/')) {
    const [month, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}`;
  }

  return dateStr;
}

/**
 * Formats a date range string from start and end dates
 * @param startDate The start date (ISO format, YYYY-MM format, or MM/YYYY format)
 * @param endDate The end date (ISO format, YYYY-MM format, or MM/YYYY format), omit for ongoing
 * @returns Formatted date range string
 */
export function formatDateRange(startDate?: string | Date | null, endDate?: string | Date | null): string {
  if (!startDate) return '';

  const normalizedStart = normalizeDate(startDate);
  const normalizedEnd = endDate ? normalizeDate(endDate) : '';

  return normalizedEnd ? `${normalizedStart} - ${normalizedEnd}` : `${normalizedStart} - Present`;
}

/**
 * Parses a date string into month and year components
 * @param dateString The date string (ISO format, YYYY-MM format, or MM/YYYY format)
 * @returns Object with month and year as strings
 */
export function parseDateString(dateString?: string | Date | null): { month: string; year: string } {
  if (!dateString) {
    return { month: '', year: '' };
  }

  const normalizedDate = normalizeDate(dateString);
  if (!normalizedDate) {
    return { month: '', year: '' };
  }

  const [year, month] = normalizedDate.split('-');
  return { month, year };
}

/**
 * Creates a date string in YYYY-MM format from month and year components
 * @param month The month as a string
 * @param year The year as a string
 * @returns Date string in YYYY-MM format
 */
export function createDateString(month: string, year: string): string {
  if (!month || !year) return '';
  return `${year}-${month.padStart(2, '0')}`;
}

/**
 * Normalizes year input so 2-digit years map to a full year.
 * Rules:
 * - 4 digits: unchanged
 * - 2 digits <= current year's last two digits: 2000 + value
 * - 2 digits > current year's last two digits: 1900 + value
 * @param yearInput The typed year input
 * @returns Normalized 4-digit year as string, or original trimmed input if unsupported
 */
export function normalizeYearInput(yearInput: string): string {
  const trimmed = yearInput.trim();
  if (!/^\d{2,4}$/.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.length === 4) {
    return trimmed;
  }

  const twoDigitYear = parseInt(trimmed, 10);
  const currentYearTwoDigits = new Date().getFullYear() % 100;
  const fullYear =
    twoDigitYear <= currentYearTwoDigits
      ? 2000 + twoDigitYear
      : 1900 + twoDigitYear;

  return fullYear.toString();
}

/**
 * Converts a Date object to YYYY-MM-DD string using local date parts to avoid UTC timezone shift
 * @param d The Date object
 * @returns Date string in YYYY-MM-DD format
 */
export function toLocalDateString(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// alternative : const toLocalDateString = (d: Date): string =>
//   new Intl.DateTimeFormat('sv-SE', { timeZone: 'UTC' }).format(d);


/**
 * Wraps a local Date into a UTC Date with the same calendar date (no hour shift)
 * @param d The Date object with local date parts
 * @returns Date object representing UTC midnight of the same calendar date
 */
export function toUTCDate(d: Date): Date {
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}

/**
 * Formats a full calendar date (ISO string) as a short display date, e.g. "15 Mar 2026".
 * Unlike the month/year helpers above (for CV work-experience ranges), this is for
 * fields that carry a full day/month/year — e.g. availability, allocation, or work start/end dates.
 * @param value ISO date string, or null/undefined
 * @returns Formatted date, or "—" when value is missing or unparseable
 */
export function formatShortDate(value?: string | null): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}